<template>
  <div class="sub-account">
    <label class="emphasis sub-title">
      <span>{{ $t('settings.user.vendor.subAccounts') }}</span>
      <span class="sub-title-hint">({{ $t('settings.user.vendor.subAccountsHint', [maxNumOfVendorSubAccount]) }})</span>
    </label>
    <VendorSubList
      ref="vendorSubList"
      :accountId="accountInfo.vendorId"
      :accountName="accountInfo.name"
      :allowInvite="hasAddPerm"
      :allowEdit="editSub"
      :allowEditSwp="!!swpEnabled"
      :activeDepartmentId="activeDepartmentId"
      @close:edit="hideEdit => enableSubEdit(false, hideEdit)"
      @allow:add="val => allowAdd = val"
      @hide:edit="val => hideSubEdit = val"
    >
      <template
        v-if="isContractorAdmin && hasAddPerm"
        slot="add-sub-account"
      >
        <DialogSlot
          v-if="allowAdd"
          id="settings-users-contractor-add-subaccount"
          ref="add-sub-account"
          :modalBtn="addSubAccLabel"
          :modalHeader="addSubAccLabel"
          :disableBtn="editSub"
          :showCloseBtn="false"
        >
          <div
            id="addSubAccModal"
            slot="content"
          >
            <label class="emphasis required-flag"> {{ $t('settings.user.vendor.name') }}</label>
            <input
              v-model="subAccount.name"
              v-hkldinput
              v-fusioninputcheck="{
                type: 'string',
                pattern: regex.nonEmptyString,
                tip: $t('hint.inputTip.string'),
                required: true,
                classes: 'tooltip-class'}"
              type="text"
              required
              @change="validateSubAccount()"
            >

            <label class="emphasis required-flag"> {{ $t('settings.user.vendor.email') }}</label>
            <input
              v-model.trim="subAccount.username"
              v-hkldinput
              v-fusioninputcheck="{
                type: 'string',
                pattern: regex.email,
                tip: $t('hint.inputTip.email'),
                required: true,
                classes: 'tooltip-class'}"
              :class="usernameErrClass"
              type="text"
              required
              @change="validateSubAccount()"
            >

            <label class="emphasis">{{ $t('settings.user.vendor.allowPerms') }}</label>
            <div
              v-if="enableTenderFeature"
              class="check-perm"
            >
              <input
                id="settings-users-contractor-subPerms-tender"
                v-hkldinput
                type="checkbox"
                :checked="subAccount.perms[Rcs.ContractorTender]"
                @change="event => addPerms(event.target.checked, Rcs.ContractorTender)"
              >
              <label
                for="settings-users-contractor-subPerms-tender"
                class="contractor-perm"
              >
                {{ subAccTableElements[Rcs.ContractorTender] }}
              </label>
            </div>
            <div
              v-if="enableProjectFeature"
              class="check-perm"
            >
              <input
                id="settings-users-contractor-subPerms-project"
                v-hkldinput
                type="checkbox"
                :checked="subAccount.perms[Rcs.ContractorProject]"
                @change="event => addPerms(event.target.checked, Rcs.ContractorProject)"
              >
              <label
                for="settings-users-contractor-subPerms-project"
                class="contractor-perm"
              >
                {{ subAccTableElements[Rcs.ContractorProject] }}
              </label>
            </div>
            <div
              v-if="enableWorkPermitsFeature"
              class="check-perm"
            >
              <input
                id="settings-users-contractor-subPerms-swp"
                v-hkldinput
                type="checkbox"
                :checked="subAccount.perms[Rcs.StandaloneWorkPermits]"
                :disabled="!swpEnabled"
                @change="event => addPerms(event.target.checked, Rcs.StandaloneWorkPermits)"
              >
              <label
                for="settings-users-contractor-subPerms-swp"
                class="contractor-perm"
              >
                {{ subAccTableElements[Rcs.StandaloneWorkPermits] }}
              </label>
            </div>

            <div
              v-if="enableEcircularFeature"
              class="check-perm"
            >
              <input
                id="settings-users-admin-vendor-contacts"
                v-hkldinput
                type="checkbox"
                :checked="subAccount.perms[Rcs.ContractorSettings]"
                @change="event => addPerms(event.target.checked, Rcs.ContractorSettings)"
              >
              <label
                for="settings-users-admin-vendor-contacts"
                class="contractor-perm"
              >
                {{ subAccTableElements[Rcs.ContractorSettings] }}
              </label>
            </div>

            <div v-if="!enableTenderFeature && !enableProjectFeature && !enableWorkPermitsFeature && !enableEcircularFeature">
              {{ $t('emptyValue') }}
            </div>
          </div>
          <div slot="buttons">
            <button
              v-hkldreset
              type="button"
              class="btn btn--orange"
              role="cancel-button"
              @click="() => closeModal()"
            >{{ $t('action.cancel') }}</button>
            <button
              v-hkldreset
              type="button"
              class="btn btn--darkblue"
              :disabled="disableAddSub"
              role="new-button"
              @click="() => addSubAccount()"
            >{{ $t('action.add') }}</button>
          </div>
        </DialogSlot>
        <button
          v-show="!hideSubEdit"
          type="button"
          class="btn btn--orange"
          role="edit-button"
          @click="() => enableSubEdit(true)"
        >
          {{ $t('action.edit') }}
        </button>
      </template>
    </VendorSubList>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import { ref, unref, getCurrentInstance, computed } from 'vue-demi'
import EventBus from '@/utils/eventBus'
import * as accountsRequest from '@/api/accounts/request'
import * as regex from '@/utils/regex'
import { UserDomain } from '@/utils/consts'
import DialogSlot from '@/controls/DialogSlot'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { Rcs } from '@/utils/aclHelper'
import { getMaxNumOfVendorSubAccount } from '@/api/settings/request'
import { addSubAccountPerm, ContractorSubAccPermsMap, vendorSubAccTableElements } from './consts'
import VendorSubList from './VendorSubList'

export default {
  components: {
    VendorSubList,
    DialogSlot,
  },
  inject: [
    'getAccountInfo',
    'getSwpEnabled',
    'getActiveDepartmentId',
  ],
  setup () {
    const { proxy: that } = getCurrentInstance()
    const subAccount = ref({ perms: {} })
    const disableAddSub = ref(true)
    const emailInvalid = ref(false)
    const accountInfo = computed(() => {
      return that.getAccountInfo()
    })
    const validateEmailIfProjectRep = async () => {
      const username = lodash.get(that, 'subAccount.username')
      if (!username) {
        emailInvalid.value = true
        return false
      }
      const companyId = unref(accountInfo).vendorId
      const result = await accountsRequest.checkIfProjectRep(companyId, username)
      emailInvalid.value = result
      return result
    }
    const validate = async () => {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'false'}, '#addSubAccModal')
      const isAllValidated = fusionInputCheckUtil.isAllFieldsValid('#addSubAccModal')
      const isUsedOnProjectRep = await validateEmailIfProjectRep()
      return isAllValidated && !isUsedOnProjectRep
    }
    const resetSubAcc = () => {
      subAccount.value = { perms: {} }
      disableAddSub.value = true
      emailInvalid.value = false
    }
    return {
      subAccount,
      disableAddSub,
      accountInfo,
      usernameErrClass: computed(() => {
        return { 'is-invalid-input': unref(emailInvalid) }
      }),
      validate,
      resetSubAcc,
    }
  },
  data () {
    return {
      regex,
      allowAdd: true,
      editSub: false,
      hideSubEdit: false,
      maxNumOfVendorSubAccount: 0,
      subAccTableElements: vendorSubAccTableElements(),
      Rcs,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isContractorAdmin']),
    ...mapGetters('regions', [
      'enableTenderFeature',
      'enableProjectFeature',
      'enableWorkPermitsFeature',
      'enableEcircularFeature',
    ]),
    hasAddPerm () {
      return !!lodash.get(this.currentUser, ['perms', addSubAccountPerm.resource])
    },
    addSubAccLabel () {
      return `${this.$t('action.add')} ${this.$t('settings.user.vendor.subAccounts')}`
    },
    activeDepartmentId () {
      return this.getActiveDepartmentId()
    },
    swpEnabled () {
      return this.getSwpEnabled()
    },
  },
  async mounted () {
    this.maxNumOfVendorSubAccount = await getMaxNumOfVendorSubAccount()
  },
  methods: {
    enableSubEdit (enabled, hideEditBtn = true) {
      this.editSub = enabled
      this.hideSubEdit = hideEditBtn
    },
    validateSubAccount () {
      this.disableAddSub = !(this.subAccount && this.subAccount.name && this.subAccount.username)
    },
    addPerms (checked, permType) {
      if (!this.subAccount || !this.subAccount.perms) {
        this.subAccount = { ...this.subAccount, perms: {} }
      }
      this.subAccount.perms = {
        ...this.subAccount.perms,
        [permType]: checked ? ContractorSubAccPermsMap[permType] : undefined,
      }
    },
    getSubAccount () {
      return lodash.cloneDeep(this.subAccount)
    },
    closeModal () {
      this.resetSubAcc()
      fusionInputCheckUtil.resetAllFields()
      this.$refs['add-sub-account'].closeModal()
    },
    async addSubAccount () {
      const isValid = await this.validate()
      if (!isValid) return
      const subAccount = this.getSubAccount()
      const request = {
        user: {
          ...subAccount,
          isAdmin: false,
          domain: UserDomain.CMS,
          userType: 421,
          externalId: this.accountInfo.vendorId,
          departmentIds: [this.activeDepartmentId],
        },
        contact: {
          NAME: [{ description: 'Name', value: subAccount.name.trim() }],
          EMAIL: [{ description: 'Email', value: subAccount.username.trim() }],
        },
      }
      await accountsRequest.createAccount(request)
      this.closeModal()
      EventBus.emit('refresh-and-invite:vendor-sub-accounts')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.sub-title {
  margin-bottom: 15px;
}
.sub-title-hint {
  font-weight: normal;
}

.sub-account {
  .align-right {
    padding: 0;
  }
  .dialog-slot {
    float: right;
    margin-bottom: 5px;
  }
  button {
    float: right;
    margin: 0 5px 5px;
  }
}
.contractor-perm {
  font-weight: normal;
  margin-top: 5px;
}
</style>
